var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect, useRef } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { getMessages, pollUndoUntilSettled } from '../services/chatApi';
import { ensureAgentBadge, normalizeHistoryMessage } from '../helpers/historyHelpers';
import { dedupeMessagesByResponseId, settleUndoInMessage } from '../helpers/streamHelpers';
export const MESSAGES_KEY_PREFIX = ['ai-chat', 'messages'];
const PAGE_SIZE = 50;
/** Replay every settle this conversation has seen over a freshly fetched page. */
function applySettledTokens(messages, settledTokens) {
    if (settledTokens.size === 0)
        return messages;
    return messages.map((message) => settleUndoInMessage(message, settledTokens));
}
export function useChatMessages(conversationId) {
    const queryClient = useQueryClient();
    const queryKey = [...MESSAGES_KEY_PREFIX, conversationId];
    // Undo tokens settled per conversation, kept outside the query cache so a
    // settle is not lost when the cache is empty or does not yet hold the card.
    //
    // Keyed BY conversation rather than reset on switch. `getMessages` takes no
    // AbortSignal, so a request issued for conversation A can still land after
    // the user has moved to B; a single ref cleared on switch would have been
    // emptied by then, and A's page would be cached with a live Undo card on an
    // operation already reversed. React Query keeps that cache, and the 30s
    // staleTime means returning to A can serve it without refetching.
    const settledTokensByConversation = useRef(new Map());
    const tokensFor = useCallback((id) => {
        const key = id !== null && id !== void 0 ? id : '';
        let tokens = settledTokensByConversation.current.get(key);
        if (!tokens) {
            tokens = new Set();
            settledTokensByConversation.current.set(key, tokens);
        }
        return tokens;
    }, []);
    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error, isPreviousData } = useInfiniteQuery({
        queryKey,
        queryFn: (_a) => __awaiter(this, [_a], void 0, function* ({ pageParam }) {
            if (!conversationId)
                return { messages: [], next_cursor: undefined };
            const page = yield getMessages(conversationId, { before: pageParam, limit: PAGE_SIZE });
            // Apply every settle this conversation has seen BEFORE the page reaches
            // the cache. A history request already in flight when `undo_settled`
            // arrives was read from the server before the reversal, so landing it
            // untouched would put a live Undo back on a card we have just settled —
            // the exact stale-offer this ticket exists to remove. Settles are
            // terminal, so replaying them over a fresh page can only ever settle.
            return Object.assign(Object.assign({}, page), { messages: applySettledTokens(page.messages, tokensFor(conversationId)) });
        }),
        getNextPageParam: (lastPage) => { var _a; return (_a = lastPage.next_cursor) !== null && _a !== void 0 ? _a : undefined; },
        enabled: Boolean(conversationId),
        keepPreviousData: Boolean(conversationId),
        refetchOnWindowFocus: false,
        staleTime: 30000
    });
    // Flatten pages into a single array — oldest first
    // Pages come in reverse-chronological order (newest page first), so we reverse
    // Deduplicate by response identity to handle race conditions between the SSE
    // optimistic append and the API fetch. A durable-approval continuation turn
    // is both persisted to history (id = ai_messages UUID) and replayed/appended
    // live (id === response_id); de-duping on `id` alone would leave both visible
    // (IP-14386). dedupeMessagesByResponseId collapses them, keeping the persisted
    // copy. Ensure agent messages have agent_badge block (history may omit it).
    const messages = data
        ? dedupeMessagesByResponseId(data.pages
            .slice()
            .reverse()
            .flatMap((page) => page.messages))
            .map(normalizeHistoryMessage)
            .map(ensureAgentBadge)
        : [];
    // ---------------------------------------------------------------------------
    // Cache mutation helpers — optimistic updates to the infinite query cache
    // ---------------------------------------------------------------------------
    const appendMessage = useCallback((message) => {
        queryClient.setQueryData(queryKey, (old) => {
            if (!old) {
                return {
                    pages: [{ messages: [message], next_cursor: undefined }],
                    pageParams: [undefined]
                };
            }
            const alreadyExists = old.pages.some((page) => page.messages.some((m) => m.id === message.id));
            if (alreadyExists)
                return old;
            const pages = old.pages.map((page, i) => (i === 0 ? Object.assign(Object.assign({}, page), { messages: [...page.messages, message] }) : page));
            return Object.assign(Object.assign({}, old), { pages });
        });
    }, [queryClient, queryKey]);
    const replaceMessage = useCallback((tempId, message) => {
        queryClient.setQueryData(queryKey, (old) => {
            if (!old)
                return old;
            const pages = old.pages.map((page) => (Object.assign(Object.assign({}, page), { messages: page.messages.map((m) => (m.id === tempId ? message : m)) })));
            return Object.assign(Object.assign({}, old), { pages });
        });
    }, [queryClient, queryKey]);
    const updateMessage = useCallback((id, updater) => {
        queryClient.setQueryData(queryKey, (old) => {
            if (!old)
                return old;
            const pages = old.pages.map((page) => (Object.assign(Object.assign({}, page), { messages: page.messages.map((m) => (m.id === id ? updater(m) : m)) })));
            return Object.assign(Object.assign({}, old), { pages });
        });
    }, [queryClient, queryKey]);
    const settleUndo = useCallback((actionIds) => {
        const settledTokens = actionIds instanceof Set ? actionIds : new Set(actionIds);
        if (settledTokens.size === 0)
            return;
        // Remember them for this conversation even when nothing is on screen yet.
        // The cache can be empty (first load still in flight) or not yet contain
        // the card, and a settle that only ever wrote to the cache would be lost
        // in both cases.
        const remembered = tokensFor(conversationId);
        settledTokens.forEach((token) => remembered.add(token));
        queryClient.setQueryData(queryKey, (old) => {
            if (!old)
                return old;
            let changed = false;
            const pages = old.pages.map((page) => {
                let pageChanged = false;
                const settledMessages = page.messages.map((message) => {
                    const settled = settleUndoInMessage(message, settledTokens);
                    if (settled !== message)
                        pageChanged = true;
                    return settled;
                });
                if (!pageChanged)
                    return page;
                changed = true;
                return Object.assign(Object.assign({}, page), { messages: settledMessages });
            });
            // Tokens that name nothing on screen leave the cache object identical,
            // so an event for another tab's card — or a repeat of one we already
            // applied — costs nothing and cannot re-render the list.
            return changed ? Object.assign(Object.assign({}, old), { pages }) : old;
        });
    }, [queryClient, queryKey]);
    // History only restores the running state; the click's poll died on reload.
    // Resume that same read, keyed by the Rails-issued operation, without a POST.
    const resumedUndoPolls = useRef(new Map());
    useEffect(() => () => {
        resumedUndoPolls.current.forEach((controller) => controller.abort());
        resumedUndoPolls.current.clear();
    }, [conversationId]);
    useEffect(() => {
        if (!conversationId || !data || isPreviousData)
            return;
        const operations = new Map();
        data.pages.forEach((page) => page.messages.forEach((message) => {
            var _a;
            (_a = message.envelope) === null || _a === void 0 ? void 0 : _a.blocks.forEach((block) => {
                var _a;
                const undo = block.type === 'undo' ? block : block.undoData;
                const operation = undo === null || undo === void 0 ? void 0 : undo.undo_bulk_operation_id;
                if ((undo === null || undo === void 0 ? void 0 : undo.undo_in_progress) && !undo.undone && operation && /^\d+$/.test(operation)) {
                    const tokens = (_a = operations.get(operation)) !== null && _a !== void 0 ? _a : new Set();
                    tokens.add(undo.action_id);
                    operations.set(operation, tokens);
                }
            });
        }));
        operations.forEach((tokens, operation) => {
            if (resumedUndoPolls.current.has(operation))
                return;
            const controller = new AbortController();
            resumedUndoPolls.current.set(operation, controller);
            const onProgress = (progress) => {
                if (controller.signal.aborted)
                    return;
                queryClient.setQueryData(queryKey, (old) => old && Object.assign(Object.assign({}, old), { pages: old.pages.map((page) => (Object.assign(Object.assign({}, page), { messages: page.messages.map((message) => {
                            if (!message.envelope)
                                return message;
                            return Object.assign(Object.assign({}, message), { envelope: Object.assign(Object.assign({}, message.envelope), { blocks: message.envelope.blocks.map((block) => {
                                        if (block.type === 'undo' && tokens.has(block.action_id) && !block.undone) {
                                            return Object.assign(Object.assign({}, block), { undo_progress: progress });
                                        }
                                        if (block.undoData && tokens.has(block.undoData.action_id) && !block.undoData.undone) {
                                            return Object.assign(Object.assign({}, block), { undoData: Object.assign(Object.assign({}, block.undoData), { undo_progress: progress }) });
                                        }
                                        return block;
                                    }) }) });
                        }) }))) }));
            };
            void pollUndoUntilSettled(conversationId, operation, { signal: controller.signal, onProgress })
                .then(() => {
                if (!controller.signal.aborted)
                    settleUndo(tokens);
            })
                .catch((pollError) => {
                // Failure/partial/timeout is not a completed reversal. Keep the card
                // unsettled and avoid launching a new poll on each cache render.
                if (!controller.signal.aborted)
                    console.error('[useChatMessages] Undo progress unavailable', pollError);
            });
        });
    }, [conversationId, data, isPreviousData, queryClient, settleUndo]);
    const seedMessages = useCallback((targetConversationId, msgs) => {
        const targetKey = [...MESSAGES_KEY_PREFIX, targetConversationId];
        queryClient.setQueryData(targetKey, {
            pages: [{ messages: msgs, next_cursor: undefined }],
            pageParams: [undefined]
        });
    }, [queryClient]);
    const clearMessages = useCallback(() => {
        queryClient.removeQueries({ queryKey });
    }, [queryClient, queryKey]);
    return {
        messages,
        isLoading,
        isFetchingNextPage,
        hasNextPage: hasNextPage !== null && hasNextPage !== void 0 ? hasNextPage : false,
        fetchNextPage: () => {
            void fetchNextPage();
        },
        error: error instanceof Error ? error : null,
        appendMessage,
        replaceMessage,
        updateMessage,
        seedMessages,
        clearMessages,
        settleUndo
    };
}
